import { Notify } from "@/components/ui/notify";

interface Issue {
  line?: number;
  column?: string | null;
  reason?: string;
  severity?: string;
  error?: string;
}

/**
 * Spec 082 — as linhas que não entraram, e por quê. O número é o do ARQUIVO, como o gestor o vê no
 * Excel: um índice interno não o levaria a lugar nenhum.
 *
 * Aviso e erro convivem na mesma lista porque os dois são coisas que ele precisa saber; o que muda
 * é se a linha entrou ou não, e isso vai dito em cada item.
 */
export function ImportIssueList({ issues }: { issues: unknown }) {
  if (!issues) return null;

  // A falha do arquivo inteiro chega como um objeto com `error`, não como lista de linhas.
  if (!Array.isArray(issues)) {
    const msg = (issues as Issue).error;
    return msg ? (
      <Notify.Banner variant="danger" title="A importação não foi executada" description={msg} />
    ) : null;
  }

  const rows = issues as Issue[];
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <h4 className="text-[14px] font-medium text-text-primary">Linhas com observação</h4>
      <div className="max-h-64 overflow-y-auto rounded-md border border-border-default">
        <table className="w-full text-[12px]">
          <caption className="sr-only">Linhas do arquivo com aviso ou erro</caption>
          <thead className="border-b border-border-default bg-bg-muted-rd uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="px-2 py-1.5 text-left">Linha</th>
              <th className="px-2 py-1.5 text-left">Coluna</th>
              <th className="px-2 py-1.5 text-left">Situação</th>
              <th className="px-2 py-1.5 text-left">Motivo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i, idx) => (
              <tr key={`${i.line}-${idx}`} className="border-b border-border-default last:border-0">
                <td className="px-2 py-1.5 font-mono">{i.line ?? "—"}</td>
                <td className="px-2 py-1.5">{i.column ?? "—"}</td>
                <td className="px-2 py-1.5">
                  {i.severity === "WARNING" ? "Entrou, com aviso" : "Não entrou"}
                </td>
                <td className="px-2 py-1.5">{i.reason ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
